/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import {
  addResponseMessage,
  addUserMessage,
  renderCustomComponent,
  setQuickButtons,
  toggleMsgLoader
} from 'react-chat-widget';
import { v4 as uuidv4 } from 'uuid';
import Card from '../components/Card';
import AppointmentDateCarousel from '../components/cs/AppointmentDateCarousel/AppointmentDateCarousel';
import LocationResultCarousel from '../components/cs/LocationResultCarousel/LocationResultCarousel';
import TimeSlotGrid from '../components/cs/TimeSlotGrid/TimeSlotGrid';
import GoogleMap from '../components/GoogleMap';
import { CS } from '../constants/botTypes';
import { CS_APPOINTMENT_DATES, CS_LOCATION_RESULTS } from '../constants/carouselTypes';
import { CS_APPOINTMENT_TIMES } from '../constants/gridTypes';
import {
  CARD,
  CAROUSEL,
  DATA,
  GRID,
  MAPS,
  PAUSE,
  REPLY,
  SAY,
  WEBSOCKET
} from '../constants/messageTypes';
import languages from '../constants/translations';
import { getLocationsFromClinicList } from './cs-helpers';
import WebSocketUtils from './websocket-helpers';

let resetCurrentFlowIdentifier = null;
let SESSION_ID = '';
let BOT_ID = '';
let BOT_TYPE = '';
let MESSAGING_ENDPOINT = '';
let WEBSOCKET_ENDPOINT = '';
let SEARCH_PARAMS = null;
let PAUSE_OVERRIDE = null;
let INITALIZED = false;

let flowIdentifier = '';
let language = 'en';
let timeslotGridUpdater = null;
let isLoadingMessageShown = false;
let initialLoadCallback = null;

let websocket = null;
let messages = [];

export const registerTimeslotGridUpdater = updater => {
  timeslotGridUpdater = updater;
};

export const unregisterTimeslotGridUpdater = () => {
  timeslotGridUpdater = null;
};

export const registerInitialLoadCallback = callback => {
  initialLoadCallback = callback;
};

// Messaging helpers in their own file so that we can send jEllie messages from any component that imports the below function

/**
 * sendMessageToBot prepares the POST /messages payload, sends it, and processes the response to render new bot messages
 * Call this function when you need to communicate with jEllie.
 *
 * @param  {string} messageType
 * @param  {any} messageBody
 * @param  {Function} setCurrentFlowIdentifier DataContext setCurrentFlowIdentifier function
 * @param  {string} sessionId
 * @param  {string} botType
 * @param  {URLSearchParams} searchParams
 * @param  {string} messagingEndpoint
 * @param  {string} websocketEndpoint
 */
export function sendMessageToBot(
  messageType,
  messageBody,
  setCurrentFlowIdentifier = resetCurrentFlowIdentifier,
  sessionId = SESSION_ID,
  botId = BOT_ID,
  botType = BOT_TYPE,
  searchParams = SEARCH_PARAMS,
  messagingEndpoint = MESSAGING_ENDPOINT,
  websocketEndpoint = WEBSOCKET_ENDPOINT
) {
  // only need to pass all args on START bot call
  if (!INITALIZED) {
    resetCurrentFlowIdentifier = setCurrentFlowIdentifier;
    SESSION_ID = sessionId;
    BOT_TYPE = botType;
    MESSAGING_ENDPOINT = messagingEndpoint;
    WEBSOCKET_ENDPOINT = websocketEndpoint;
    SEARCH_PARAMS = searchParams;
    PAUSE_OVERRIDE = SEARCH_PARAMS.get('po');
    INITALIZED = true;
  }

  const payload = {
    sessionId,
    botId,
    searchParams,
    botType
  };

  const actionId = messageBody?.parent?.id || messageBody?.id;

  if (actionId) {
    const message = { action: actionId };

    if (messageBody?.data && Object.keys(messageBody.data).length > 0) {
      message.data = { ...messageBody.data };
    }

    Object.assign(payload, { message });
  }

  postNewMessage(createMessagePayload(payload));
}

// Send payload to endpoint, handle responses
async function postNewMessage(data) {
  try {
    // Show animated loading dots before sending request
    toggleMsgLoader();
    isLoadingMessageShown = true;

    const response = await fetch(MESSAGING_ENDPOINT, {
      method: 'POST',
      headers: {
        ...data.headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data.payload)
    });
    await processStream(response);
  } catch {
    // Clear loading message on error
    removeLoadingMessage();
    addResponseMessage(languages[language]?.technicalErrorMessage);
  }
}

const processStream = async response => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  // let pausing = false;
  let buffer = '';
  let streamDone = false;

  while (!streamDone) {
    const result = await reader.read();

    streamDone = result.done;

    if (streamDone) {
      // Decode any remaining bytes.
      buffer += decoder.decode();

      if (buffer.trim()) {
        const message = JSON.parse(buffer);

        await addMessage(message);
      }

      break;
    }

    buffer += decoder.decode(result.value, {
      stream: true
    });

    const lines = buffer.split('\n');

    // Keep the incomplete line for the next chunk.
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }

      const message = JSON.parse(line);

      await addMessage(message);
    }
  }

  // Clear loading message after stream completes
  removeLoadingMessage();
};

async function addMessage(message) {
  if (message.type === PAUSE && PAUSE_OVERRIDE !== 'true') {
    await handlePause(message);
  }
  messages.push(message);
  processMessageResponse(message);
}

async function handlePause(message) {
  await new Promise(resolve => {
    setTimeout(resolve, Number(message?.body) * 1000);
  });
}

function processMessageResponse(message) {
  let locations;
  let clinics;

  // Call initial load callback when first real message arrives
  if (initialLoadCallback) {
    initialLoadCallback();
    initialLoadCallback = null; // Clear callback so it's only called once
  }

  const { type, body } = message;

  switch (type) {
    case CARD:
      handleCard(body);
      break;
    case SAY:
      handleSay(message, body);
      break;
    case REPLY:
      if (body === 'Español') {
        language = 'es';
      }
      addUserMessage(body);
      break;
    case MAPS:
      if (BOT_TYPE === CS) {
        clinics = body;
        locations = getLocationsFromClinicList(clinics);
      }
      handleMaps(locations);
      break;
    case CAROUSEL:
      handleCarousel(message, language);
      break;
    case GRID:
      handleGrid(message, language);
      break;
    case WEBSOCKET:
      if (body === 'close-conn') {
        disconnectWebSocket();
      }
      break;
    default:
      break;
  }
}

export const createMessagePayload = ({ sessionId, botId, message, searchParams }) => {
  // Web channel session, web channel bot message, web channel time zone, web channel bot type, web channel params
  let headers = {
    SessionId: sessionId,
    ChatbotId: botId
  };

  // Send clinicId as request headers if there are any
  if (searchParams.toString() !== '') {
    const clinicId = searchParams.get('clinicId');
    const walkIn = searchParams.get('walkIn');
    const ref = searchParams.get('ref');
    if (clinicId) {
      Object.assign(headers, { ClinicId: clinicId });
    }

    if (walkIn) {
      Object.assign(headers, { WalkIn: walkIn });
    }

    if (ref) {
      Object.assign(headers, { Ref: ref });
    }
  }

  return { payload: { ...message }, headers };
};

const handleCard = body => {
  renderCustomComponent(Card, {
    body,
    handleCardActionSubmit: event => {
      sendMessageToBot(DATA, event);
      disableCardActions(event);
    },
    handleCardActionOpenUrl: event => {
      window.open(event.url, '_blank', 'noopener,noreferrer');

      // Only send to server and disable actions if the action has an ID
      if (event.data?.id !== null) {
        sendMessageToBot(DATA, event);
        disableCardActions(event);
      }

      // Start websocket connection on open_form action
      if (event.id === 'open_form') {
        connectWebSocket(SESSION_ID);
      }
    }
  });
  // Scroll to card after render
  requestAnimationFrame(() => {
    const msgContainer = document.getElementById('messages');
    if (msgContainer) {
      const allRenderedMessages = msgContainer.getElementsByClassName('ac-adaptiveCard');
      const mostRecentMessage = allRenderedMessages[allRenderedMessages.length - 1];
      if (mostRecentMessage) {
        mostRecentMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });
};

// Remove the animated loading dots
const removeLoadingMessage = () => {
  if (!isLoadingMessageShown) return;

  toggleMsgLoader();
  isLoadingMessageShown = false;
};

const handleSay = (message, body) => {
  checkForRerenderingComponents(body);
  addResponseMessage(body);
  if (message.replies && message.replies.length > 0) {
    setQuickButtons(message.replies);
  }
};

const checkForRerenderingComponents = body => {
  // if these messages change in jEllie, change them here too
  const rerenderComponentTriggers = [
    'Thanks! Please select a location.',
    'Please confirm your location.',
    'Click on a location number on the map to select it.',
    "OK! Let's find a different appointment. Please enter a ZIP code.",
    'Appointment is no longer available, please choose another time.',
    'Sorry, the last appointment was just booked. Please try searching again.',
    'There was a problem scheduling your appointment, please try again.',
    'Got it! Click on a date to see available times. Scroll right to see more dates.'
  ];
  if (rerenderComponentTriggers.includes(body)) {
    /**
     * CS bot has prompts for user to re-search clinics or re-pick time.
     * In the event of a re-search/re-pick (meaning complex components have already rendered once)
     * Need to hide the old react components that have been rendered
     * To not cause unwanted multiple-render side effects
     **/
    flowIdentifier = uuidv4();
    resetCurrentFlowIdentifier(flowIdentifier);
  }
};

const handleMaps = locations => {
  renderCustomComponent(GoogleMap, {
    botType: BOT_TYPE,
    locations,
    flowIdentifier
  });
};

const handleCarousel = (message, language) => {
  let clinics;
  let locations;
  switch (message.carouselType) {
    case CS_LOCATION_RESULTS:
      if (BOT_TYPE === CS) {
        clinics = message.body;
        locations = getLocationsFromClinicList(clinics);
      }
      renderCustomComponent(LocationResultCarousel, {
        locations,
        clinics,
        flowIdentifier,
        language
      });
      break;
    case CS_APPOINTMENT_DATES:
      renderCustomComponent(AppointmentDateCarousel, {
        clinic: message.body,
        flowIdentifier,
        language
      });
      break;
    default:
      break;
  }
  // needed to have the parent rcw message not display flex. messes up 3rd party carousel lib
  // Using slick-slider presence to target the correct message since batched rendering may have
  // added subsequent card messages by the time this setTimeout fires
  setTimeout(() => {
    const allMessages = document.getElementsByClassName('rcw-message');
    for (const msg of allMessages) {
      if (msg.querySelector('.slick-slider') && !msg.classList.contains('carousel-component')) {
        msg.classList.add('carousel-component');
      }
    }
  });
};

const handleGrid = (message, language) => {
  switch (message.gridType) {
    case CS_APPOINTMENT_TIMES: {
      if (timeslotGridUpdater) {
        // Update existing grid in place — preserves Back/Start Over card position
        timeslotGridUpdater(message.body);
      } else {
        renderCustomComponent(TimeSlotGrid, {
          timeslots: message.body,
          flowIdentifier,
          language
        });
      }
      break;
    }
    default:
      break;
  }
};

export const disableAllCardActions = () => {
  const messagesContainer = document.getElementById('messages');
  const actions = messagesContainer.querySelectorAll('.ac-pushButton');
  const iconButtons = messagesContainer.querySelectorAll('.ac-image');
  const dateCards = messagesContainer.querySelectorAll('.appointment-date-wrapper');

  if (actions?.length) {
    actions.forEach(actionItem => {
      actionItem.disabled = true;
      actionItem.classList.add('ac-pushButton-disabled');
    });
  }
  if (iconButtons?.length) {
    iconButtons.forEach(iconButton => {
      iconButton.removeAttribute('role');
      iconButton.setAttribute('tabindex', '-1');
      iconButton.setAttribute('aria-disabled', 'true');

      iconButton.onclick = null;
      iconButton.style.pointerEvents = 'none';

      iconButton.classList.remove('ac-selectable');
      iconButton.classList.add('ac-image-disabled');
    });
  }
  if (dateCards?.length) {
    dateCards.forEach(dateCard => {
      dateCard.setAttribute('tabindex', '-1');
      dateCard.setAttribute('aria-disabled', 'true');
      dateCard.style.pointerEvents = 'none';
      dateCard.classList.add('disabled');
    });
  }

  const timeSlots = messagesContainer.querySelectorAll('.time-slot');
  if (timeSlots?.length) {
    timeSlots.forEach(timeSlot => {
      timeSlot.setAttribute('tabindex', '-1');
      timeSlot.setAttribute('aria-disabled', 'true');
      timeSlot.style.pointerEvents = 'none';
      timeSlot.classList.add('disabled');
    });
  }

  const submissionButtons = messagesContainer.querySelectorAll('.submission-button');
  if (submissionButtons?.length) {
    submissionButtons.forEach(btn => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    });
  }
};

const disableCardActions = cardAction => {
  const { data } = cardAction;
  const messagesContainer = document.getElementById('messages');
  console.log('icons', messagesContainer.querySelectorAll('.ac-image'));

  switch (true) {
    case data?.card?.includes('CARD_COMMERCIAL_INSURANCE'):
      if (
        data['relationship-to-cardholder'] &&
        data['relationship-to-cardholder'] === 'OTHER' &&
        !data['relationship-to-cardholder-other']
      ) {
        return;
      }
      break;
    default:
      disableAllCardActions();
      break;
  }
};

export function connectWebSocket(sessionId) {
  if (websocket) {
    return;
  }

  const isProd = process.env.REACT_APP_ENV === 'prod';
  const wsServerUrl = isProd
    ? process.env.REACT_APP_WEBSOCKET_SERVER_URL_PROD
    : process.env.REACT_APP_WEBSOCKET_SERVER_URL_STAGING;

  websocket = new WebSocketUtils(wsServerUrl, sessionId);

  websocket.connect();
  websocket.subscribe('chatbot_updates', async result => {
    if (result) {
      const message = JSON.parse(result.data);
      await addMessage(message);
    }
  });
}

export async function recoverWebsocketMessages(sessionId) {
  try {
    const response = await fetch(`${WEBSOCKET_ENDPOINT}?sessionId=${sessionId}`, {
      method: 'GET'
    });
    if (response) {
      await processStream(response);
    }
  } catch (error) {
    console.log('Error recovering websocket messages', error);
  }
}

const disconnectWebSocket = () => {
  if (websocket) {
    websocket.destroy();
    websocket = null;
  }
};
